import { auth } from '../firebase-config';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { handleGoogleLogin, handleGoogleLogout, initGoogleClient } from './google/config';
import { handleLogin, handleLogout, microsoftAuthService, msalInstance } from './outlook/config';
import { dark_mode_primary, dark_mode_secondary, AppMode, oAuthStatus, AuthProvider, settingsTableHeaders, settingsTableBody, paymentMethods, AuthStatus, PaymentTiers, light_mode_secondary, dark_mode_hover } from './values';

export function Settings({ appMode, toggleAppMode, setUser, accounts, setAccounts, setLoading, setError, isGoogleLoggedIn, isMicrosoftLoggedIn, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, authManager, setAuthStatus, activeProviderAccounts }) {
    const settings = [
        { label: 'My Account', content: <MyAccount appMode={appMode} toggleAppMode={toggleAppMode} setAuthStatus={setAuthStatus} />, icon: <></> },
        { label: 'Billing and Payments', content: <Billings appMode={appMode} />, icon: <></> },
        { label: 'Notifications', content: <Notifications appMode={appMode} />, icon: <></> },
        { label: 'Integrations', content: <Integrations appMode={appMode} setUser={setUser} setAccounts={setAccounts} setLoading={setLoading} setError={setError} isGoogleLoggedIn={isGoogleLoggedIn} setIsGoogleLoggedIn={setIsGoogleLoggedIn} isMicrosoftLoggedIn={isMicrosoftLoggedIn} setIsMicrosoftLoggedIn={setIsMicrosoftLoggedIn} authManager={authManager} accounts={accounts} activeProviderAccounts={activeProviderAccounts} />, icon: <></> },
    ];
    return <SettingsTabs appMode={appMode} settings={settings} />;
}

const SettingsTabs = ({ settings, appMode }) => {
    const [activeTab, setActiveTab] = useState(0);
    const handleClick = (index) => setActiveTab(index);
    return (
        <div className="flex h-screen">
            <div className={`h-full ${appMode === AppMode.LIGHT ? 'bg-gray-50 border-r border-gray-200' : dark_mode_secondary} flex flex-col transition-all duration-300 ease-in-out w-1/5 shadow-md`}>
                <div className={`p-6 ${appMode === AppMode.LIGHT ? 'border-b border-gray-200' : 'border-b border-gray-700'}`}>
                    <h2 className={`text-xl font-semibold ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>Settings</h2>
                </div>
                <div className='flex-1 flex flex-col justify-center overflow-y-auto px-3'>
                    {settings.map((setting, index) => (
                        <div key={index} className={`flex items-center p-4 my-1 cursor-pointer rounded-lg transition-all duration-200 
                                ${index === activeTab
                                ? appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-600 shadow-sm' : 'bg-transparent text-white hover:bg-gray-700'
                                : appMode === AppMode.LIGHT ? 'text-slate-600 hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-700'}`
                        } onClick={() => handleClick(index)}>
                            <span className="tab-icon">{setting.icon}</span>
                            <span className="ml-3 font-medium">{setting.label}</span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex-grow overflow-y-auto">{settings[activeTab].content}</div>
        </div>
    );
};

function MyAccount({ appMode, toggleAppMode, setAuthStatus }) {
    const [userEmail, setUserEmail] = useState(null);
    const signOut = async () => {
        try {
            await signOut(auth);
            setAuthStatus(AuthStatus.NOT_LOGGED_IN);
        }
        catch (err) { }
    };

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserEmail(user.email);
            } else {
                setUserEmail(null);
            }
        });
        return () => unsubscribe();
    }, []);

    return (
        <div className={`h-screen overflow-y-auto px-8 py-6 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary}`}>
            <div className="mb-8">
                <h1 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>Account Settings</h1>
                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-slate-500' : 'text-gray-300'}`}>Manage your personal information and preferences</p>
            </div>
            <div className={`mb-8 p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Email Address</h2>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <p className={appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-200'}>Your email address is <strong>{userEmail}</strong></p>
                    <button className={`mt-2 sm:mt-0 px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'text-blue-600 hover:bg-blue-50' : 'text-blue-400 hover:bg-transparent'} transition-colors`}>Change Email</button>
                </div>
            </div>
            <div className={`mb-8 p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>App Theme</h2>
                <div className="flex items-center justify-between">
                    <p className={appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-200'}>Current theme: <strong>{appMode === AppMode.LIGHT ? 'Light' : 'Dark'}</strong></p>
                    <button onClick={toggleAppMode} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'} transition-colors`}>Switch to {appMode === AppMode.LIGHT ? 'Dark' : 'Light'} Mode</button>
                </div>
            </div>
            <div className={`mb-8 p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Password</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Current Password</label>
                        <div className={`relative flex overflow-hidden rounded-md border ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'} transition focus-within:border-blue-600`}>
                            <input type="password" id="login-password" className={`w-full py-2 px-4 ${appMode === AppMode.LIGHT ? 'bg-white text-gray-700' : 'bg-transparent text-white'} focus:outline-none`} placeholder="***********" />
                        </div>
                    </div>
                    <div>
                        <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>New Password</label>
                        <div className={`relative flex overflow-hidden rounded-md border ${appMode === AppMode.LIGHT ? 'border-gray-300' : 'border-gray-600'} transition focus-within:border-blue-600`}>
                            <input type="password" id="login-password1" className={`w-full py-2 px-4 ${appMode === AppMode.LIGHT ? 'bg-white text-gray-700' : 'bg-transparent text-white'} focus:outline-none`} placeholder="***********" />
                        </div>
                    </div>
                </div>
                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>Can't remember your current password? <span className={`font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'} cursor-pointer`}>Recover Account</span></p>
                <button className={`mt-4 px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'} text-white hover:opacity-90 transition-opacity`}>Save Password</button>
            </div>
            <div className={`mb-8 p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Sign Out</h2>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <p className={`mb-4 sm:mb-0 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-200'}`}>Sign out of your account</p>
                    <button onClick={signOut} className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors">Sign Out</button>
                </div>
            </div>
            <div className={`mb-8 p-6 rounded-lg ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Delete Account</h2>
                <div className="mb-4 inline-flex items-center rounded-md bg-red-100 px-4 py-2 text-red-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg> Proceed with caution
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <p className={`mb-4 sm:mb-0 w-3/4 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-200'}`}>Make sure you have taken backup of your account. We will completely wipe your data. There is no way to access your account after this action.</p>
                    <button className="bg-red-500 w-1/5 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors">Delete Account</button>
                </div>
            </div>
        </div>
    )
}

function Billings({ appMode }) {
    const PaymentMethod = ({ icon, name }) => {
        return (
            <div className={`p-3 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                <i className={`bi bi-${icon} mr-2`}></i> {name}
            </div>
        );
    }
    return (
        <div className={`h-screen overflow-y-auto px-8 py-6 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary}`}>
            <div className="mb-8">
                <h1 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>Billing Settings</h1>
                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-slate-500' : 'text-gray-300'}`}>Manage your payment information and billing history</p>
            </div>
            <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                <div className="grid gap-8 md:grid-cols-2">
                    <div className="space-y-6">
                        <div>
                            <div className="flex items-center mb-3">
                                <h3 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Billing Period</h3>
                                <button className={`ml-auto text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'} hover:underline`}>Change</button>
                            </div>
                            <div className={`flex items-center rounded-md p-4 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'} shadow-sm`}>
                                <div>
                                    <strong className={`block text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>MONTHLY</strong>
                                    <span className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Next Renewal: April 13, 2025</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center mb-3">
                                <h3 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Payment Method</h3>
                                <button className={`ml-auto text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600' : 'text-blue-400'} hover:underline`}>Change</button>
                            </div>
                            <div className={`flex items-center rounded-md p-4 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'} shadow-sm`}>
                                <div className="h-10 w-14 bg-blue-600 rounded-md mr-4 flex items-center justify-center text-white font-bold">VISA</div>
                                <div>
                                    <strong className={`block text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>**** **** **** 453</strong>
                                    <strong className={`block font-medium ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>ALBERT K. DANIEL</strong>
                                    <span className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Expires on: Dec 2025</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Name</label>
                            <input className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} type="text" value="Shakir Ali" />
                        </div>
                        <div>
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Email Address</label>
                            <input className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} type="text" value="shakir.ali@corpora.de" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Billing Address</label>
                            <input className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} type="text" value="82844 Boyle Extension Suite 541 - Covington, HI / 28013" />
                        </div>
                        <div>
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>VAT #</label>
                            <input className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} type="text" value="6346322" />
                        </div>
                        <div>
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Country</label>
                            <input className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} type="text" value="Germany" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={`mb-8 overflow-hidden rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? 'border border-gray-200' : 'border border-gray-700'}`}>
                <p className={`py-3 px-4 text-lg font-medium ${appMode === AppMode.LIGHT ? 'bg-gray-100 text-gray-800' : dark_mode_secondary + ' text-white'}`}>Billing History</p>
                <div className={`overflow-x-auto ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary}`}>
                    <table className="w-full">
                        <thead className={appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}>
                            <tr>{settingsTableHeaders.map((header, index) => <th key={index} className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{header}</th>)}</tr>
                        </thead>
                        <tbody className={`divide-y ${appMode === AppMode.LIGHT ? 'divide-gray-200' : 'divide-gray-700'}`}>
                            {settingsTableBody.map((element, index) => <tr key={index}>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{element.date}</td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{element.invoice}</td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-900' : 'text-white'}`}>{element.amount}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{element.status}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <button className={`font-medium ${appMode === AppMode.LIGHT ? 'text-blue-600 hover:text-blue-800' : 'text-blue-400 hover:text-blue-300'}`}>{element.actions}</button>
                                </td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Payment Methods</h2>
                <div className="flex flex-wrap gap-3">{paymentMethods.map((method, index) => <PaymentMethod key={index} name={method.name} icon={method.icon} />)}</div>
                <button className={`mt-4 px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}>Add Payment Method</button>
            </div>
        </div>
    )
}

function Notifications({ appMode }) {
    return (
        <div className={`h-screen overflow-y-auto px-8 py-6 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary}`}>
            <div className="mb-8">
                <h1 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>Notification Settings</h1>
                <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-slate-500' : 'text-gray-300'}`}>Manage how and when you receive notifications</p>
            </div>
            <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Email Notifications</h2>
                <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>New Messages</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Receive notifications when you get new messages</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked />
                            <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                        </label>
                    </div>
                </div>
                <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Calendar Events</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Receive notifications about upcoming events</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked />
                            <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                        </label>
                    </div>
                </div>
                <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>System Updates</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Receive notifications about system updates and maintenance</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" />
                            <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                        </label>
                    </div>
                </div>
            </div>
            <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Push Notifications</h2>
                <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Desktop Notifications</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Show notifications on your desktop</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" defaultChecked />
                            <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                        </label>
                    </div>
                </div>
                <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Sound Alerts</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Play sound when notifications arrive</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" />
                            <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                        </label>
                    </div>
                </div>
            </div>
            <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Notification Frequency</h2>
                <div className={`mb-6 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`}>
                    <p className="mb-2">How often would you like to receive notification digests?</p>
                    <div className="flex flex-wrap gap-3 mt-3">
                        <div className={`px-4 py-2 rounded-md cursor-pointer ${appMode === AppMode.LIGHT ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}>Real-time</div>
                        <div className={`px-4 py-2 rounded-md cursor-pointer ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-300 text-gray-700' : 'bg-transparent border border-gray-600 text-gray-200'}`}>Daily</div>
                        <div className={`px-4 py-2 rounded-md cursor-pointer ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-300 text-gray-700' : 'bg-transparent border border-gray-600 text-gray-200'}`}>Weekly</div>
                    </div>
                </div>
                <div className={`mb-4 ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-200'}`}>
                    <p className="mb-2">Quiet hours</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>From</label>
                            <input type="time" defaultValue="22:00" className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} />
                        </div>
                        <div>
                            <label className={`block mb-2 text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>To</label>
                            <input type="time" defaultValue="07:00" className={`w-full rounded-md border p-2 ${appMode === AppMode.LIGHT ? 'bg-white border-gray-300 text-gray-700' : 'bg-transparent border-gray-600 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end">
                <button className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}>Save Changes</button>
            </div>
        </div>
    );
}

export function Integrations({ appMode, setUser, accounts, setAccounts, setLoading, setError, isGoogleLoggedIn, isMicrosoftLoggedIn, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, authManager, activeProviderAccounts }) {
    const history = useHistory();
    const upgradePromptRef = useRef(null);
    const accountOptionsRef = useRef(null);
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [showAccountOptions, setShowAccountOptions] = useState(false);
    const [syncSettings, setSyncSettings] = useState({
        emailSync: true,
        calendarSync: true,
        filesSync: true,
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (upgradePromptRef.current && !upgradePromptRef.current.contains(event.target)) {
                setShowUpgradePrompt(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleAuthStateChange = () => {
            if (authManager) {
                setAccounts(authManager.getLoggedInAccounts());
                setUpdateTrigger(prev => prev + 1);
            }
        };

        if (authManager) {
            setAccounts(authManager.getLoggedInAccounts());
        }

        window.addEventListener('authStateChanged', handleAuthStateChange);
        authManager.addStateChangeListener(handleAuthStateChange);
        return () => {
            window.removeEventListener('authStateChanged', handleAuthStateChange);
            authManager.removeStateChangeListener(handleAuthStateChange);
        };//eslint-disable-next-line
    }, [setAccounts]);

    useEffect(() => {
        if (authManager) {
            setAccounts(authManager.getLoggedInAccounts());
        }//eslint-disable-next-line
    }, [updateTrigger, setAccounts]);

    useEffect(() => {
        if (authManager) {
            try {
                if (typeof authManager.getSubscriptionInfo === 'function') {
                    setSubscriptionInfo(authManager.getSubscriptionInfo());
                } else {
                    const accountsCount = authManager.getLoggedInAccounts().length;
                    setSubscriptionInfo({
                        tier: PaymentTiers.FREE,
                        name: 'Free',
                        accountLimit: 2,
                        accountsUsed: accountsCount,
                        canAddMore: accountsCount < 2
                    });
                }
            } catch (error) {
                setSubscriptionInfo({
                    tier: PaymentTiers.FREE,
                    name: 'Free',
                    accountLimit: 2,
                    accountsUsed: 0,
                    canAddMore: true,
                });
            }
        }//eslint-disable-next-line
    }, [updateTrigger, isGoogleLoggedIn, isMicrosoftLoggedIn]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (accountOptionsRef.current && !accountOptionsRef.current.contains(event.target)) {
                setShowAccountOptions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const savedSettings = localStorage.getItem('syncSettings');
        if (savedSettings) {
            try {
                setSyncSettings(JSON.parse(savedSettings));
            } catch (error) { }
        }
    }, []);

    const canAddMoreAccounts = useCallback(() => {
        if (!authManager) return true;
        try {
            if (typeof authManager.canAddMoreAccounts === 'function') {
                return authManager.canAddMoreAccounts();
            } else {
                return authManager.getLoggedInAccounts().length < 2;
            }
        } catch (error) {
            return false;
        }//eslint-disable-next-line
    }, []);

    const handleGoogleLoginClick = async () => {
        try {
            if (!canAddMoreAccounts()) {
                setShowUpgradePrompt(true);
                return;
            }
            setLoading(true);
            await initGoogleClient();
            if (authManager) {
                const result = await handleGoogleLogin(authManager, setUser, setLoading, setError, setIsGoogleLoggedIn);
                if (result) {
                    authManager.setActiveAccount(result.id);
                    authManager.notifyStateChange();
                    setUpdateTrigger(prev => prev + 1);
                }
            } else {
                await handleGoogleLogin(null, setUser, setLoading, setError, setIsGoogleLoggedIn);
            }
        } catch (error) {
            if (error.message && error.message.includes("Account limit reached")) {
                setShowUpgradePrompt(true);
            } else {
                setError(`Google login failed: ${error.message}`);
            }
            setLoading(false);
        }
    };

    const handleMicrosoftLogin = async () => {
        try {
            if (!canAddMoreAccounts()) {
                setShowUpgradePrompt(true);
                return;
            }
            setLoading(true);
            await microsoftAuthService.initialize();
            await microsoftAuthService.syncAccounts();
            if (authManager) {
                const result = await handleLogin(setUser, setLoading, setError, setIsMicrosoftLoggedIn);
                if (result) {
                    const msAccounts = authManager.getAccounts(AuthProvider.MICROSOFT);
                    const newAccount = msAccounts.find(acc =>//eslint-disable-next-line
                        acc.userData && acc.userData.email === result.email ||//eslint-disable-next-line
                        acc.userData && acc.userData.username === result.email
                    );
                    if (newAccount) {
                        authManager.setActiveAccount(newAccount.id);
                        const msalAccounts = msalInstance.getAllAccounts();
                        const matchingMsalAccount = msalAccounts.find(acc =>
                            acc.username === newAccount.userData?.username ||
                            acc.username === newAccount.userData?.email
                        );
                        if (matchingMsalAccount) {
                            msalInstance.setActiveAccount(matchingMsalAccount);
                        }
                    }
                    authManager.notifyStateChange();
                    setUpdateTrigger(prev => prev + 1);
                }
            } else {
                await handleLogin(setUser, setLoading, setError, setIsMicrosoftLoggedIn);
            }
        } catch (e) {
            if (e.message && e.message.includes("Account limit reached")) {
                setShowUpgradePrompt(true);//eslint-disable-next-line
            } else if (e.message && e.message.includes("MSAL account not found") ||//eslint-disable-next-line
                e.message && e.message.includes("Session expired")) {
                setError("Your Microsoft session has expired. Please try again.");
                try {
                    localStorage.removeItem('MicrosoftUserData');
                    localStorage.removeItem('MicrosoftAuth');
                    await microsoftAuthService.initialize(true);
                }
                catch (cleanupError) { }
            } else {
                setError(e.message);
            }
            setLoading(false);
        }
    };

    const handleAccountLogout = async (accountId) => {
        try {
            setLoading(true);
            setShowAccountOptions(false);
            const account = authManager.getAccounts().find(acc => acc.id === accountId);
            if (!account) {
                throw new Error("Account not found");
            }
            let result = false;
            if (account.provider === AuthProvider.MICROSOFT) {
                result = await handleLogout(accountId, setUser, setLoading, setError, setIsMicrosoftLoggedIn);
                if (result) {
                    try {
                        await microsoftAuthService.initialize();
                        const msalAccounts = msalInstance.getAllAccounts();
                        const msalAccount = msalAccounts.find(msAcc =>
                            msAcc.username === account.userData?.username ||
                            msAcc.username === account.userData?.email ||
                            (account.userData?.localAccountId && msAcc.localAccountId === account.userData.localAccountId) ||
                            (account.userData?.homeAccountId && msAcc.homeAccountId === account.userData.homeAccountId)
                        );
                        if (msalAccount) {
                            try {
                                msalInstance.setActiveAccount(msalAccount);
                                await msalInstance.logoutRedirect({
                                    account: msalAccount,
                                    onRedirectNavigate: () => false,
                                });
                            } catch (logoutError) {
                                await msalInstance.logoutRedirect({
                                    account: msalAccount,
                                    postLogoutRedirectUri: window.location.origin
                                });
                            }
                        }
                    }
                    catch (msalError) { }
                }
            } else if (account.provider === AuthProvider.GOOGLE) {
                result = await handleGoogleLogout(authManager, accountId, setUser, setLoading, setError, setIsGoogleLoggedIn);
            }
            if (result) {
                if (authManager.getAccounts().some(acc => acc.id === accountId)) {
                    authManager.removeAccount(accountId);
                }
                try {
                    const savedAccounts = localStorage.getItem('AuthAccounts');
                    if (savedAccounts) {
                        const parsedAccounts = JSON.parse(savedAccounts);
                        const updatedAccounts = parsedAccounts.filter(acc => acc.id !== accountId);
                        if (parsedAccounts.length !== updatedAccounts.length) {
                            localStorage.setItem('AuthAccounts', JSON.stringify(updatedAccounts));
                        }
                    }
                    if (account.provider === AuthProvider.GOOGLE) {
                        const legacyData = localStorage.getItem('GoogleUserData');
                        if (legacyData) {
                            try {
                                const parsedData = JSON.parse(legacyData);
                                if (parsedData &&
                                    parsedData.email === account.userData?.email) {
                                    localStorage.removeItem('GoogleUserData');
                                    localStorage.removeItem('GoogleAuth');
                                }
                            } catch (e) { }
                        }
                    } else if (account.provider === AuthProvider.MICROSOFT) {
                        const legacyData = localStorage.getItem('MicrosoftUserData');
                        if (legacyData) {
                            try {
                                const parsedData = JSON.parse(legacyData);
                                if (parsedData &&
                                    parsedData.username === account.userData?.username) {
                                    localStorage.removeItem('MicrosoftUserData');
                                    localStorage.removeItem('MicrosoftAuth');
                                }
                            } catch (e) { }
                        }
                    }
                    const activeAccounts = localStorage.getItem('ActiveAuthAccounts');
                    if (activeAccounts) {
                        const parsedActiveAccounts = JSON.parse(activeAccounts);
                        if (parsedActiveAccounts[account.provider] === accountId) {
                            const remainingAccounts = authManager.getAccounts().filter(
                                acc => acc.provider === account.provider && acc.id !== accountId
                            );
                            if (remainingAccounts.length > 0) {
                                parsedActiveAccounts[account.provider] = remainingAccounts[0].id;
                            } else {
                                delete parsedActiveAccounts[account.provider];
                            }
                            localStorage.setItem('ActiveAuthAccounts', JSON.stringify(parsedActiveAccounts));
                        }
                    }
                    const tokens = localStorage.getItem('AuthTokens');
                    if (tokens) {
                        const parsedTokens = JSON.parse(tokens);
                        if (parsedTokens[accountId]) {
                            delete parsedTokens[accountId];
                            localStorage.setItem('AuthTokens', JSON.stringify(parsedTokens));
                        }
                    }
                }
                catch (storageError) { }
                authManager.notifyStateChange();
                setUpdateTrigger(prev => prev + 1);
                setAccounts(authManager.getLoggedInAccounts());
            }
        } catch (e) {
            setError(`Logout failed: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgradeRedirect = () => {
        setShowUpgradePrompt(false);
        history.push('/pricing');
    };

    const handleSyncSettingChange = (setting) => {
        const newSettings = { ...syncSettings, [setting]: !syncSettings[setting] };
        setSyncSettings(newSettings);
        localStorage.setItem('syncSettings', JSON.stringify(newSettings));
    };

    if (isGoogleLoggedIn === oAuthStatus.LOGGED_IN || isMicrosoftLoggedIn === oAuthStatus.LOGGED_IN) {
        return (
            <div className={`h-screen overflow-y-auto px-8 py-6 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary}`}>
                <div className="mb-8">
                    <h1 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>Integration Settings</h1>
                    <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-slate-500' : 'text-gray-300'}`}>Manage your connected accounts and services</p>
                </div>
                {subscriptionInfo && (
                    <div className={`mb-8 p-6 rounded-lg shadow ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                        <div className="flex justify-between items-center">
                            <h2 className={`text-xl font-semibold ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Current Plan: {subscriptionInfo.name}</h2>
                            {subscriptionInfo.tier !== PaymentTiers.ENTERPRISE && <button onClick={handleUpgradeRedirect} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors`}>Upgrade Plan</button>}
                        </div>
                        <div className="mt-4">
                            <p className={`${appMode === AppMode.LIGHT ? 'text-slate-600' : 'text-gray-300'}`}>Account usage: {subscriptionInfo.accountsUsed} of {subscriptionInfo.accountLimit === 15 ? 'Unlimited' : subscriptionInfo.accountLimit} accounts</p>
                            <div className="w-full bg-gray-300 rounded-full h-2.5 mt-2">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${subscriptionInfo.accountLimit === 15 ? '100' : Math.min(100, (subscriptionInfo.accountsUsed / subscriptionInfo.accountLimit) * 100)}%` }}></div>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`mb-8 p-6 rounded-lg shadow ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary}`}>
                    <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Connected Accounts</h2>
                    <div className="space-y-4">
                        {accounts.map((account, index) => (
                            <div key={account.id || index} className={`p-4 rounded-md ${activeProviderAccounts && activeProviderAccounts[account.provider] && activeProviderAccounts[account.provider].id === account.id
                                ? appMode === AppMode.LIGHT
                                    ? account.provider === AuthProvider.GOOGLE ? 'bg-red-500 border border-red-200' : 'bg-blue-500 border border-blue-200'
                                    : account.provider === AuthProvider.GOOGLE ? 'bg-red-700 border border-red-800' : 'bg-blue-700 border border-blue-800'
                                : appMode === AppMode.LIGHT
                                    ? account.provider === AuthProvider.GOOGLE ? 'bg-red-600 border border-gray-20' : 'bg-blue-600 border border-gray-200'
                                    : account.provider === AuthProvider.GOOGLE ? 'bg-red-700 border border-red-600' : 'bg-blue-700 border border-blue-600'
                                }`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className={`w-10 h-10 rounded-full ${account.provider === AuthProvider.GOOGLE ? 'bg-red-100' : 'bg-blue-100'} flex items-center justify-center`}>
                                            <i className={`bi ${account.provider === AuthProvider.GOOGLE ? 'bi-google text-red-500' : 'bi-microsoft text-blue-500'} text-xl`}></i>
                                        </div>
                                        <div className="ml-4">
                                            <h3 className={`font-medium text-white`}>
                                                {account.provider === AuthProvider.GOOGLE
                                                    ? account.userData?.name || account.userData?.email || 'Google Account'
                                                    : account.userData?.name || account.userData?.username || 'Microsoft Account'
                                                }
                                            </h3>
                                            <p className={`text-sm text-gray-300`}>
                                                {account.provider === AuthProvider.GOOGLE
                                                    ? account.userData?.email || ''
                                                    : account.userData?.username || ''
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={(e) => {
                                        e.stopPropagation(); account.provider === AuthProvider.GOOGLE
                                            ? handleAccountLogout(account.id)
                                            : handleAccountLogout(account.id);
                                    }} className={`px-4 py-2 rounded-md text-gray-200 ${account.provider === AuthProvider.GOOGLE
                                        ? appMode === AppMode.LIGHT
                                            ? 'bg-red-500 hover:bg-red-700'
                                            : 'bg-red-900 hover:bg-red-800'
                                        : appMode === AppMode.LIGHT
                                            ? 'bg-blue-500 hover:bg-blue-700'
                                            : 'bg-blue-800 hover:bg-blue-900'
                                        } transition-colors`}> Disconnect
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6">
                        {subscriptionInfo && subscriptionInfo.canAddMore
                            ? <button onClick={() => setShowAccountOptions(true)} className={`flex items-center px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors`}>
                                <i className="bi bi-plus-circle mr-2"></i> Connect Another Account
                            </button>
                            : <button onClick={() => setShowUpgradePrompt(true)} className={`flex items-center px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors`}>
                                <i className="bi bi-arrow-up-circle mr-2"></i> Upgrade to Add More Accounts
                            </button>
                        }
                    </div>
                    {showAccountOptions && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div ref={accountOptionsRef} className={`w-96 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary}`}>
                                <div className="p-4 border-b flex justify-between items-center">
                                    <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Connect Another Account
                                    </h2>
                                    <button onClick={() => setShowAccountOptions(false)} className="text-gray-500 hover:text-gray-700">
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                </div>
                                <div className="p-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <button onClick={() => { setShowAccountOptions(false); handleMicrosoftLogin(); }} className={`p-3 rounded-md flex flex-col items-center ${appMode === AppMode.LIGHT ? 'bg-blue-50 hover:bg-blue-100 text-blue-600' : 'bg-blue-900 hover:bg-blue-800 text-blue-300'}`}>
                                            <i className="bi bi-microsoft text-xl mb-2"></i>
                                            <span className="text-sm">Microsoft</span>
                                        </button>
                                        <button onClick={() => { setShowAccountOptions(false); handleGoogleLoginClick(); }} className={`p-3 rounded-md flex flex-col items-center ${appMode === AppMode.LIGHT ? 'bg-red-50 hover:bg-red-100 text-red-600' : 'bg-red-900 hover:bg-red-800 text-red-300'}`}>
                                            <i className="bi bi-google text-xl mb-2"></i>
                                            <span className="text-sm">Google</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {showUpgradePrompt && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div ref={upgradePromptRef} className={`w-96 rounded-lg shadow-lg ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_secondary}`}>
                                <div className="p-4 border-b flex justify-between items-center">
                                    <h2 className={`font-semibold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Account Limit Reached</h2>
                                    <button onClick={() => setShowUpgradePrompt(false)} className="text-gray-500 hover:text-gray-700">
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                </div>
                                <div className="p-6">
                                    <div className="text-center mb-4">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-500 mb-4">
                                            <i className="bi bi-exclamation-triangle text-2xl"></i>
                                        </div>
                                        <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Subscription Limit Reached</h3>
                                        <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>You've reached the maximum number of accounts for your current {subscriptionInfo?.name} plan.</p>
                                    </div>
                                    <div className="flex justify-center space-x-3">
                                        <button onClick={() => setShowUpgradePrompt(false)} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : `bg-transparent ${dark_mode_hover} text-white`}`}>Cancel</button>
                                        <button onClick={handleUpgradeRedirect} className={`px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white`}>Upgrade Now</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                    <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Data Synchronization</h2>
                    <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Email Sync</h3>
                                <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Sync emails from connected accounts</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={syncSettings.emailSync} onChange={() => handleSyncSettingChange('emailSync')} />
                                <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                            </label>
                        </div>
                    </div>
                    <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Calendar Sync</h3>
                                <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Sync calendar events from connected accounts</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={syncSettings.calendarSync} onChange={() => handleSyncSettingChange('calendarSync')} />
                                <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                            </label>
                        </div>
                    </div>
                    <div className={`mb-4 p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <h3 className={`font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Files Sync</h3>
                                <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Sync files from connected accounts</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={syncSettings.filesSync} onChange={() => handleSyncSettingChange('filesSync')} />
                                <div className={`w-11 h-6 ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-600'} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`}></div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className={`h-screen overflow-y-auto px-8 py-6 ${appMode === AppMode.LIGHT ? 'bg-white' : dark_mode_primary}`}>
                <div className="mb-8">
                    <h1 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? 'text-slate-800' : 'text-white'}`}>Integration Settings</h1>
                    <p className={`mt-2 ${appMode === AppMode.LIGHT ? 'text-slate-500' : 'text-gray-300'}`}>Connect your accounts to enhance your experience</p>
                </div>
                {subscriptionInfo && (
                    <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                        <div className="flex justify-between items-center">
                            <h2 className={`text-xl font-semibold ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Current Plan: {subscriptionInfo.name}</h2>
                            {subscriptionInfo.tier !== PaymentTiers.ENTERPRISE && <button onClick={handleUpgradeRedirect} className={`px-4 py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors`}>Upgrade Plan</button>}
                        </div>
                        <div className="mt-4">
                            <p className={`${appMode === AppMode.LIGHT ? 'text-slate-600' : 'text-gray-300'}`}>Account usage: {subscriptionInfo.accountsUsed} of {subscriptionInfo.accountLimit === 15 ? 'Unlimited' : subscriptionInfo.accountLimit} accounts</p>
                        </div>
                    </div>
                )}
                <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                    <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Available Integrations</h2>
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className={`p-6 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                    <i className="bi bi-microsoft text-blue-500 text-2xl"></i>
                                </div>
                                <div className="ml-4">
                                    <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Microsoft</h3>
                                    <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Connect your Outlook, Calendar, and OneDrive</p>
                                </div>
                            </div>
                            <button onClick={handleMicrosoftLogin} className={`w-full py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}>Connect Microsoft Account</button>
                        </div>
                        <div className={`p-6 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                                    <i className="bi bi-google text-red-500 text-2xl"></i>
                                </div>
                                <div className="ml-4">
                                    <h3 className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Google</h3>
                                    <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Connect your Gmail, Google Calendar, and Drive</p>
                                </div>
                            </div>
                            <button onClick={handleGoogleLoginClick} className={`w-full py-2 rounded-md ${appMode === AppMode.LIGHT ? 'bg-red-500 hover:bg-red-600' : 'bg-red-600 hover:bg-red-700'} text-white transition-colors`}>Connect Google Account</button>
                        </div>
                    </div>
                </div>
                <div className={`mb-8 p-6 rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? light_mode_secondary : dark_mode_secondary}`}>
                    <h2 className={`text-xl font-semibold mb-4 ${appMode === AppMode.LIGHT ? 'text-slate-700' : 'text-white'}`}>Benefits of Integration</h2>
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className={`p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mb-3">
                                <i className="bi bi-envelope text-indigo-500 text-xl"></i>
                            </div>
                            <h3 className={`font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Email Management</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Access and manage all your emails in one place</p>
                        </div>
                        <div className={`p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-3">
                                <i className="bi bi-calendar-event text-green-500 text-xl"></i>
                            </div>
                            <h3 className={`font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Calendar Sync</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Keep all your events and appointments synchronized</p>
                        </div>
                        <div className={`p-4 rounded-md ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-200' : 'bg-transparent border border-gray-600'}`}>
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                                <i className="bi bi-people text-amber-500 text-xl"></i>
                            </div>
                            <h3 className={`font-medium mb-2 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>Contact Management</h3>
                            <p className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-300'}`}>Centralize your contacts from multiple sources</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}